import { IFetcherOptions, useFetcher } from "@module/_core/infras/hook/useFetcher";
import { useMutation } from "@tanstack/react-query";
import { CreateOrderDTO, CreatePaymentOrderDTO, HistoryOrderDTO, OrderStatusDTO } from "../dto/order";
import { OrderApi } from "../api/order";

interface UseOrderStatus {
    orderId: string;
    enabled: boolean;
    token?: string;
}

interface UseSubscriptionEndDate {
    token?: string;
    enabled: boolean;
}

interface UseOrderOptions {
    fetcherOptions?: IFetcherOptions<HistoryOrderDTO[]>;
}

export class OrderService {
    static ORDERS_KEY = "ORDERS";

    static useOrders = ({ fetcherOptions = {} }: UseOrderOptions = {}) => {
        const { data, isPending, isFetching, isError, error } = useFetcher(
            [OrderService.ORDERS_KEY],
            () => OrderApi.getOrders(),
            fetcherOptions
        );

        return {
            data,
            isPending,
            isFetching,
            isError,
            error
        };
    };

    static useOrderStatus = ({ orderId, enabled, token }: UseOrderStatus) => {
        const { data } = useFetcher<OrderStatusDTO>(
            [OrderService.ORDERS_KEY, orderId],
            () => (token ? OrderApi.getOrderStatusV2(orderId, token) : OrderApi.getOrderStatus(orderId)),
            {
                enabled
            }
        );

        const getOrderStatusMutation = useMutation({
            mutationFn: (orderId: string) =>
                token ? OrderApi.getOrderStatusV2(orderId, token) : OrderApi.getOrderStatus(orderId)
        });

        return { data, getOrderStatusMutation };
    };

    static useSubscriptionEndDate = ({ token, enabled }: UseSubscriptionEndDate) => {
        const { data, isFetching } = useFetcher<string>(
            [OrderService.ORDERS_KEY, "SUBSCRIPTION_END_DATE", token],
            () => OrderApi.getSubscriptionEndDate(token as string),
            {
                enabled: enabled && !!token
            }
        );

        return { data, isFetching };
    };

    static useOrderAction = () => {
        const checkInviterInfoMutation = useMutation({
            mutationFn: (phoneNumber: string) => OrderApi.checkInviterInformation(phoneNumber)
        });

        const createOrderMutation = useMutation({
            mutationFn: (createOrder: CreateOrderDTO) => OrderApi.createOrder(createOrder)
        });

        return { checkInviterInfoMutation, createOrderMutation };
    };

    static useOrderActionV2 = () => {
        const createOrderV2Mutation = useMutation({
            mutationFn: (createOrder: CreatePaymentOrderDTO) => OrderApi.createOrderV2(createOrder)
        });

        const checkInviterInfoByTokenMutation = useMutation({
            mutationFn: ({ token, inviterPhoneNumber }: { token: string; inviterPhoneNumber: string }) =>
                OrderApi.checkInviterInformationByToken(token, inviterPhoneNumber)
        });

        return { createOrderV2Mutation, checkInviterInfoByTokenMutation };
    };
}
